import { useState } from "react";
import { Spinner } from "../atoms/spinner";
import { GridLayout, HorizontalLayout, VerticalLayout } from "./layout";
import { motion, AnimatePresence } from "framer-motion";
import { InputForm, TextAreaForm } from "../atoms/input";
import { StandardButton } from "../atoms/buttons";
import { IconPlus, IconTrash, IconRefresh, IconCheck, IconPencil } from "@tabler/icons-react";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "./modal";

const PLACEHOLDER_IMAGES = [
    "https://placehold.co/400x300/6366f1/white?text=AI+Template+1",
    "https://placehold.co/400x300/8b5cf6/white?text=AI+Template+2",
    "https://placehold.co/400x300/ec4899/white?text=AI+Template+3",
];

export default function AIImageSettings() {
    const [currentPage, setCurrentPage] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);

    setTimeout(() => {
        setCurrentPage(1)
    }, 2500)

    const handleAddTemplate = (template) => {
        setTemplates([...templates, { ...template, id: Date.now() }]);
        setIsModalOpen(false);
    };

    const handleUpdateTemplate = (updatedTemplate) => {
        setTemplates(templates.map(t =>
            t.id === updatedTemplate.id ? updatedTemplate : t
        ));
        setEditingTemplate(null);
        setIsModalOpen(false);
    };

    const handleDeleteTemplate = (id) => {
        setTemplates(templates.filter(t => t.id !== id));
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingTemplate(null);
    };

    return <VerticalLayout className="settings-card" style={{width: "unset", padding: "1rem", marginBottom: "0.2rem", gap: "1rem" }}>
        <AnimatePresence mode="wait">
            { currentPage == 0 && <motion.div key="spinnerAISet01" exit={{opacity: 0}}>
                <SpinnerBox/>
            </motion.div>}
            { currentPage == 1 && <motion.div key="spinnerAISet02" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
                <TemplateTopBar
                    templates={templates}
                    onOpenModal={() => setIsModalOpen(true)}
                    onDeleteTemplate={handleDeleteTemplate}
                    onEditTemplate={handleEditTemplate}
                />
            </motion.div>}
        </AnimatePresence>
        <TemplateCreationModal
            visible={isModalOpen}
            onDismiss={handleCloseModal}
            onSave={editingTemplate ? handleUpdateTemplate : handleAddTemplate}
            editingTemplate={editingTemplate}
        />
    </VerticalLayout>
}

function TemplateTopBar({ templates, onOpenModal, onDeleteTemplate, onEditTemplate }) {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredTemplates = templates.filter(t =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return <VerticalLayout style={{ width: "auto", gap: "1rem", minHeight: "60vh"}} className="standard-margin">
        <HorizontalLayout style={{"gap": "1rem"}}>
            <InputForm
                style={{width: "100%", marginBottom: "0px"}}
                placeholder={"Zoeken door templates"}
                value={searchQuery}
                typeInput={setSearchQuery}
            />
        </HorizontalLayout>
        <TemplateGrid
            templates={filteredTemplates}
            onAddClick={onOpenModal}
            onDeleteTemplate={onDeleteTemplate}
            onEditTemplate={onEditTemplate}
        />
    </VerticalLayout>
}

function TemplateGrid({ templates, onAddClick, onDeleteTemplate, onEditTemplate }) {
    return <div>
        <GridLayout columnsTemplate="1fr 1fr 1fr 1fr" rowTemplate="repeat(140px)" gap="1rem">
            <AddTemplateButton onClick={onAddClick} />
            {templates.map(template => (
                <TemplateCard
                    key={template.id}
                    template={template}
                    onDelete={() => onDeleteTemplate(template.id)}
                    onEdit={() => onEditTemplate(template)}
                />
            ))}
        </GridLayout>
    </div>
}

function AddTemplateButton({ onClick }) {
    return <div
        onClick={onClick}
        style={{
            height: "140px",
            width: "100%",
            borderRadius: "1rem",
            border: "4px dashed gray",
            display: "flex",
            cursor: "pointer",
            transition: "border-color 0.2s ease"
        }}
        className="center"
        onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--primary-600)"}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = "gray"}
    >
        <IconPlus size={64} color="gray"/>
    </div>
}

function TemplateCard({ template, onDelete, onEdit }) {
    const [isHovered, setIsHovered] = useState(false);

    return <div
        style={{
            height: "140px",
            width: "100%",
            borderRadius: "1rem",
            overflow: "hidden",
            position: "relative",
            cursor: "pointer"
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onEdit}
    >
        <img
            src={template.previewImage}
            alt={template.title}
            style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
            }}
        />
        <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "0.5rem",
            background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
            color: "white",
            fontSize: "13px",
            fontWeight: "500"
        }}>
            {template.title}
        </div>
        {isHovered && (
            <HorizontalLayout style={{
                position: "absolute",
                top: "0.5rem",
                left: "0.5rem",
                gap: "0.25rem"
            }}>
                <div
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    style={{
                        backgroundColor: "var(--primary-500)",
                        borderRadius: "50%",
                        padding: "0.3rem",
                        cursor: "pointer"
                    }}
                >
                    <IconPencil size={16} color="white" />
                </div>
                <div
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    style={{
                        backgroundColor: "var(--error-500)",
                        borderRadius: "50%",
                        padding: "0.3rem",
                        cursor: "pointer"
                    }}
                >
                    <IconTrash size={16} color="white" />
                </div>
            </HorizontalLayout>
        )}
    </div>
}

function TemplateCreationModal({ visible, onDismiss, onSave, editingTemplate }) {
    const [step, setStep] = useState(1);
    const [title, setTitle] = useState("");
    const [prompt, setPrompt] = useState("");
    const [validationError, setValidationError] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const isEditing = !!editingTemplate;

    if (visible && editingTemplate && !isInitialized) {
        setTitle(editingTemplate.title);
        setPrompt(editingTemplate.prompt);
        setPreviewImage(editingTemplate.previewImage);
        setIsInitialized(true);
    }

    if (!visible && isInitialized) {
        setIsInitialized(false);
    }

    const resetModal = () => {
        setStep(1);
        setTitle("");
        setPrompt("");
        setValidationError("");
        setIsGenerating(false);
        setPreviewImage(null);
        setIsInitialized(false);
    };

    const handleDismiss = () => {
        resetModal();
        onDismiss();
    };

    const validatePrompt = (promptText) => {
        const matches = promptText.match(/\{\{prompt\}\}/g);
        if (!matches) {
            return "De prompt moet minimaal één {{prompt}} placeholder bevatten";
        }
        if (matches.length > 1) {
            return "De prompt mag maximaal één {{prompt}} placeholder bevatten";
        }
        return "";
    };

    const handlePromptChange = (value) => {
        setPrompt(value);
        setValidationError(validatePrompt(value));
    };

    const handleGeneratePreview = () => {
        const error = validatePrompt(prompt);
        if (error) {
            setValidationError(error);
            return;
        }
        if (!title.trim()) {
            setValidationError("Titel is verplicht");
            return;
        }
        setValidationError("");
        setIsGenerating(true);

        setTimeout(() => {
            const randomImage = PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)];
            setPreviewImage(randomImage);
            setIsGenerating(false);
            setStep(2);
        }, 2000);
    };

    const handleRegeneratePreview = () => {
        setIsGenerating(true);
        setTimeout(() => {
            const randomImage = PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)];
            setPreviewImage(randomImage);
            setIsGenerating(false);
        }, 2000);
    };

    const handleAcceptPreview = () => {
        const templateData = {
            title,
            prompt,
            previewImage
        };
        if (isEditing) {
            templateData.id = editingTemplate.id;
        }
        onSave(templateData);
        resetModal();
    };

    const handleRejectPreview = () => {
        setStep(1);
        setPreviewImage(null);
    };

    const getModalTitle = () => {
        if (step === 2) return "Preview beoordelen";
        return isEditing ? "Template bewerken" : "Nieuwe template aanmaken";
    };

    return <Modal
        visible={visible}
        dismissable={handleDismiss}
        size="medium"
    >
        <ModalTitle
            title={getModalTitle()}
            hasXmark={true}
            dismissable={handleDismiss}
        />
        <ModalBody>
            {step === 1 ? (
                <TemplateFormStep
                    title={title}
                    prompt={prompt}
                    validationError={validationError}
                    isGenerating={isGenerating}
                    onTitleChange={setTitle}
                    onPromptChange={handlePromptChange}
                />
            ) : (
                <TemplatePreviewStep
                    previewImage={previewImage}
                    isGenerating={isGenerating}
                    onRegenerate={handleRegeneratePreview}
                />
            )}
        </ModalBody>
        <ModalFooter>
            {step === 1 ? (
                <TemplateFormFooter
                    isGenerating={isGenerating}
                    hasValidationError={!!validationError}
                    isTitleEmpty={!title.trim()}
                    isPromptEmpty={!prompt.trim()}
                    onCancel={handleDismiss}
                    onGenerate={handleGeneratePreview}
                    isEditing={isEditing}
                />
            ) : (
                <TemplatePreviewFooter
                    isGenerating={isGenerating}
                    onReject={handleRejectPreview}
                    onAccept={handleAcceptPreview}
                />
            )}
        </ModalFooter>
    </Modal>
}

function TemplateFormStep({ title, prompt, validationError, isGenerating, onTitleChange, onPromptChange }) {
    return <div style={{ position: "relative" }}>
        <VerticalLayout style={{ gap: "1rem", width: "100%" }}>
            <InputForm
                title="Titel"
                placeholder="Bijv. Productfoto template"
                value={title}
                typeInput={onTitleChange}
                disabled={isGenerating}
                style={{ marginBottom: 0 }}
            />
            <VerticalLayout style={{ gap: "0.25rem", width: "100%" }}>
                <TextAreaForm
                    title="Prompt"
                    placeholder="Beschrijf de afbeelding. Gebruik {{prompt}} als placeholder voor de dynamische tekst."
                    value={prompt}
                    typeInput={onPromptChange}
                    disabled={isGenerating}
                    textAreaStyle={{ minHeight: "120px" }}
                />
                <p style={{
                    fontSize: "12px",
                    color: validationError ? "var(--error-500)" : "var(--grey-500)"
                }}>
                    {validationError || "Gebruik {{prompt}} om aan te geven waar de dynamische tekst moet komen"}
                </p>
            </VerticalLayout>
        </VerticalLayout>
        <AnimatePresence>
            {isGenerating && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        backdropFilter: "blur(4px)",
                        borderRadius: "0.5rem"
                    }}
                >
                    <Spinner />
                    <p style={{ fontSize: "13px", color: "var(--primary-600)", marginTop: "0.5rem" }}>
                        Preview wordt gegenereerd...
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
}

function TemplatePreviewStep({ previewImage, isGenerating }) {
    return <VerticalLayout style={{ gap: "1rem", width: "100%", alignItems: "center" }}>
        <div style={{
            width: "100%",
            maxWidth: "400px",
            aspectRatio: "4/3",
            borderRadius: "0.5rem",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "var(--grey-100)"
        }}>
            <img
                src={previewImage}
                alt="Preview"
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                }}
            />
            <AnimatePresence>
                {isGenerating && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.6)",
                            backdropFilter: "blur(4px)"
                        }}
                    >
                        <Spinner />
                        <p style={{ fontSize: "13px", color: "var(--primary-600)", marginTop: "0.5rem" }}>
                            Preview wordt gegenereerd...
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        <p style={{ fontSize: "14px", color: "var(--grey-700)", textAlign: "center" }}>
            Bent u tevreden met deze preview?
        </p>
    </VerticalLayout>
}

function TemplateFormFooter({ isGenerating, hasValidationError, isTitleEmpty, isPromptEmpty, onCancel, onGenerate, isEditing }) {
    return <HorizontalLayout style={{ gap: "0.5rem", marginLeft: "auto" }}>
        <StandardButton
            title="Annuleren"
            backgroundColor="grey-200"
            textColor="grey-700"
            onClick={onCancel}
            isDisabled={isGenerating}
        />
        <StandardButton
            title={isEditing ? "Preview bijwerken" : "Genereer preview"}
            onClick={onGenerate}
            isDisabled={isGenerating || hasValidationError || isTitleEmpty || isPromptEmpty}
        />
    </HorizontalLayout>
}

function TemplatePreviewFooter({ isGenerating, onReject, onAccept }) {
    return <HorizontalLayout style={{ gap: "0.5rem", marginLeft: "auto" }}>
        <StandardButton
            title="Opnieuw"
            backgroundColor="grey-200"
            textColor="grey-700"
            onClick={onReject}
            isDisabled={isGenerating}
        >
            <IconRefresh size={16} style={{ marginRight: "0.25rem" }} />
        </StandardButton>
        <StandardButton
            title="Accepteren"
            backgroundColor="success-500"
            onClick={onAccept}
            isDisabled={isGenerating}
        >
            <IconCheck size={16} style={{ marginRight: "0.25rem" }} />
        </StandardButton>
    </HorizontalLayout>
}

function SpinnerBox() {
    return <VerticalLayout style={{ width: "auto", gap: "1rem", minHeight: "60vh"}} className="standard-margin center">
        <Spinner/>
        <p style={{fontSize: "13px", color: "var(--primary-800)"}}>Gegevens ophalen...</p>
    </VerticalLayout>
}
